import { AppIcon } from '@/components/ui/app-icon';
import { PRIMARY_BUTTON_FILL } from '@/components/ui/button';
import { iconRocketLaunchSolid, iconSpinnerThird } from '@/lib/icons';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { cn } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';
import { SidebarTooltip } from './tabs/sidebar-tooltip.component';
import {
  useOpenUpgradeReady,
  useUpgradeCta,
  type UpgradeCta,
} from './use-upgrade-cta.hook';

/** The app's primary button fill (black, white in dark mode), as every
 * other primary action in the shell. */
const FILL = cn(
  PRIMARY_BUTTON_FILL,
  'transition-opacity duration-150 ease-out hover:opacity-90 active:opacity-80',
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
);

/** The focus ring's gap takes the colour of the sidebar the button sits on. */
const RING_OFFSET = 'focus-visible:ring-offset-[hsl(var(--sidebar-tint))]';

/** The days-left badge is cut out of the square by the sidebar's colour. */
const BADGE_CUTOUT =
  'shadow-[0_0_0_1.5px_hsl(var(--sidebar-tint)),0_1px_2px_rgba(16,24,40,0.2)]';

/** The rocket nudges up and to the right while its button is hovered. */
const ROCKET =
  'transition-transform duration-150 ease-out motion-safe:group-hover/upgrade:translate-x-px motion-safe:group-hover/upgrade:-translate-y-px';

/**
 * The countdown's colour: amber in a trial's last days, a shade deeper in
 * light mode, where amber-700 is under 4.5:1 on the tint; else muted.
 */
const trialTone = (cta: UpgradeCta) =>
  cta.trial?.endingSoon
    ? 'font-medium text-amber-800 dark:text-amber-400'
    : 'text-muted-foreground';

/**
 * The days-left badge, 4.5:1 or more in both themes: white on amber in a
 * trial's last days, else the page's text colour.
 */
const badgeTone = (endingSoon: boolean) =>
  endingSoon ? 'bg-amber-700 text-white' : 'bg-background text-foreground';

/**
 * The sidebar's Upgrade button, pinned above the account block: open, a status line ("Free plan · 50 credits a month", "Pro
 * trial · 9 days left" over a thin bar of the trial left, "Starter plan")
 * above a full-width primary button; folded, a 36px primary square named by
 * its tooltip, with the days left on a trial. It opens the upgrade wizard.
 * Renders nothing unless the workspace is Free, Starter or on a trial (see
 * `deriveUpgradeCta`).
 */
export function SidebarUpgradeCta({
  collapsed = false,
  className,
}: {
  collapsed?: boolean;
  className?: string;
}) {
  const cta = useUpgradeCta();
  const { open: openUpgrade, opening } = useOpenUpgradeReady();
  const posthog = usePostHog();
  if (!cta) return null;

  const rocket = (fontSize: number) =>
    opening ? (
      <AppIcon icon={iconSpinnerThird} spin aria-hidden style={{ fontSize }} />
    ) : (
      <AppIcon
        icon={iconRocketLaunchSolid}
        aria-hidden
        className={ROCKET}
        style={{ fontSize }}
      />
    );

  const onClick = () => {
    if (opening) return;
    void openUpgrade({
      source: 'sidebar',
      plan: cta.plan,
    });
    bestEffortAnalytics(() =>
      posthog?.capture('sidebar_upgrade_clicked', {
        kind: cta.kind,
        plan: cta.plan ?? null,
        collapsed,
        days_left: cta.trial?.daysLeft ?? null,
      })
    );
  };

  if (collapsed) {
    return (
      <div className={cn('grid shrink-0 place-items-center', className)}>
        <SidebarTooltip label={cta.tooltip}>
          <button
            type='button'
            aria-label={cta.tooltip}
            aria-busy={opening || undefined}
            disabled={opening}
            data-sidebar-upgrade
            onClick={onClick}
            className={cn(
              'group/upgrade relative grid h-9 w-9 place-items-center rounded-[10px]',
              FILL,
              RING_OFFSET
            )}
          >
            {rocket(15)}
            {cta.trial && (
              <span
                aria-hidden
                className={cn(
                  'absolute -top-1.5 -right-1.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] leading-none font-semibold tabular-nums',
                  BADGE_CUTOUT,
                  badgeTone(cta.trial.endingSoon)
                )}
              >
                {cta.trial.daysLeft}
              </span>
            )}
          </button>
        </SidebarTooltip>
      </div>
    );
  }

  // Clipped rather than truncated, as the nav labels are: while the rail
  // widens, the line and the label are revealed with no ellipsis flashing.
  return (
    <div
      data-sidebar-upgrade-block
      className={cn('flex shrink-0 flex-col', className)}
    >
      <p className='flex items-baseline gap-1 overflow-hidden px-2 text-xs leading-4 whitespace-nowrap'>
        <span className='shrink-0 font-medium text-foreground'>
          {cta.title}
        </span>
        {cta.detail && (
          <>
            <span aria-hidden className='text-muted-foreground'>
              ·
            </span>
            <span className={cn('tabular-nums', trialTone(cta))}>
              {cta.detail}
            </span>
          </>
        )}
      </p>
      {cta.trial && (
        <span
          role='progressbar'
          aria-label='Trial left'
          aria-valuemin={0}
          aria-valuemax={cta.trial.days}
          aria-valuenow={cta.trial.daysLeft}
          aria-valuetext={`${cta.trial.daysLeft} of ${cta.trial.days} days left`}
          className='mx-2 mt-1.5 h-[3px] overflow-hidden rounded-full bg-black/[0.08] dark:bg-white/[0.12]'
        >
          <span
            className={cn(
              'block h-full rounded-full',
              cta.trial.endingSoon ? 'bg-[hsl(var(--warning))]' : 'bg-primary'
            )}
            style={{
              width: `${(cta.trial.daysLeft / cta.trial.days) * 100}%`,
            }}
          />
        </span>
      )}
      <button
        type='button'
        aria-busy={opening || undefined}
        disabled={opening}
        data-sidebar-upgrade
        onClick={onClick}
        className={cn(
          'group/upgrade mt-2 flex h-9 w-full items-center justify-center gap-2 overflow-hidden rounded-[10px] px-3 text-[13px] leading-none font-semibold whitespace-nowrap',
          FILL,
          RING_OFFSET
        )}
      >
        {rocket(13)}
        {cta.label}
      </button>
    </div>
  );
}
